import axios from "axios";
import { END_POINT } from "config/environment";

const REF_KEY = "arno_campaign_ref";
const SESSION_KEY = "arno_campaign_session";
const UTM_KEY = "arno_campaign_utm";
const TRACKED_KEY = "arno_campaign_tracked";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type StoredRef = {
  code: string;
  at: number;
};

export type CampaignAttributionPayload = {
  campaign_ref?: string;
  campaign_session_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

const isExpired = (at: number) => Date.now() - at > TTL_MS;

export const getCampaignRef = (): string | null => {
  try {
    const raw = localStorage.getItem(REF_KEY);
    if (!raw) return null;
    const parsed: StoredRef = JSON.parse(raw);
    if (!parsed?.code || isExpired(parsed.at)) {
      clearCampaignAttribution();
      return null;
    }
    return parsed.code;
  } catch {
    return null;
  }
};

export const getCampaignSessionId = (): string | null => {
  try {
    const id = localStorage.getItem(SESSION_KEY);
    return id || null;
  } catch {
    return null;
  }
};

export const getStoredUtms = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

export const persistCampaignRef = (
  code: string,
  utms: Record<string, string> = {}
) => {
  if (!code) return;
  const normalized = code.trim().toLowerCase();
  localStorage.setItem(
    REF_KEY,
    JSON.stringify({ code: normalized, at: Date.now() })
  );
  if (Object.keys(utms).length) {
    localStorage.setItem(UTM_KEY, JSON.stringify(utms));
  }
  if (!localStorage.getItem(SESSION_KEY)) {
    localStorage.setItem(
      SESSION_KEY,
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`
    );
  }
};

export const clearCampaignAttribution = () => {
  localStorage.removeItem(REF_KEY);
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(UTM_KEY);
  localStorage.removeItem(TRACKED_KEY);
};

export const getCampaignAttributionPayload = (): CampaignAttributionPayload => {
  const ref = getCampaignRef();
  if (!ref) return {};

  const utms = getStoredUtms();
  const session = getCampaignSessionId();

  return {
    campaign_ref: ref,
    ...(session ? { campaign_session_id: session } : {}),
    ...utms,
  };
};

const extractUtms = (params: URLSearchParams): Record<string, string> => {
  const keys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ];
  const out: Record<string, string> = {};
  keys.forEach((k) => {
    const v = params.get(k);
    if (v) out[k] = v;
  });
  return out;
};

/**
 * Capture ref/utm from the current URL, persist, and record a click once per session.
 */
export const captureCampaignFromUrl = async (
  search: string = window.location.search
): Promise<string | null> => {
  const params = new URLSearchParams(search);
  const ref =
    params.get("ref") ||
    params.get("campaign_ref") ||
    params.get("campaign");

  if (!ref) {
    return getCampaignRef();
  }

  const utms = extractUtms(params);
  persistCampaignRef(ref, utms);

  const tracked = localStorage.getItem(TRACKED_KEY);
  const sessionId = getCampaignSessionId();
  if (tracked === `${ref}:${sessionId}`) {
    return ref;
  }

  try {
    const res = await axios.post(
      `${END_POINT.AUTH_URL}/campaigns/track-click`,
      {
        ref,
        landing_path: window.location.pathname + window.location.search,
        campaign_session_id: sessionId,
        ...utms,
      },
      { withCredentials: true }
    );
    const newSession = res.data?.data?.session_id;
    if (newSession) {
      localStorage.setItem(SESSION_KEY, newSession);
    }
    localStorage.setItem(TRACKED_KEY, `${ref}:${newSession || sessionId}`);
  } catch {
    // Non-blocking: signup still works without click row
  }

  return ref;
};

export const appendCampaignQuery = (url: string): string => {
  const payload = getCampaignAttributionPayload();
  if (!payload.campaign_ref) return url;

  const sep = url.includes("?") ? "&" : "?";
  const qs = new URLSearchParams();
  qs.set("campaign_ref", payload.campaign_ref);
  if (payload.campaign_session_id) {
    qs.set("campaign_session_id", payload.campaign_session_id);
  }
  return `${url}${sep}${qs.toString()}`;
};
